import React from "react";
import {
  apiFileMaster, apiFileMasterBatal, apiFileMasterFinal, apiFileMasterBatalFinal,
  apiFileMasterHapus, apiFileMasterUnduh, apiFileMasterRiwayat, formatApiErrorDetail,
} from "@/lib/api";
import { ukuranTeks, labelStatus, adaFile, finalTerbaru, lamaUnggahTeks, NAMA_JENIS, LABEL_AKSI } from "@/lib/fileMaster";
import { S, LABEL as LABEL_ANTREAN } from "@/lib/antreanUnggah";
import {
  adaPegangan, adaPilihFolder, pilihBanyak, pilihFolder, dariInput, dariDrop, ekstensiDiterima, MAKS_KUMPUL,
} from "@/lib/pilihFile";
import { useUnggahan } from "@/context/UnggahanContext";
import * as W from "@/lib/waktu";
import { Loader2, Upload, FolderUp, Download, CheckCircle2, Undo2, Trash2, History, Play, X, FileWarning } from "lucide-react";

const errText = (e) => (e?.response ? formatApiErrorDetail(e.response?.data?.detail) : e?.message) || "Terjadi kesalahan";
const TOMBOL = "inline-flex items-center gap-1 rounded border px-2.5 py-1 text-xs disabled:opacity-50";
// a folder can be chosen on a computer (picker API or webkitdirectory); phones and tablets: files only
const bisaFolder = () => {
  if (typeof window === "undefined") return false;
  const sentuhSaja = window.matchMedia?.("(pointer: coarse)").matches && !window.matchMedia?.("(any-pointer: fine)").matches;
  return !sentuhSaja && (adaPilihFolder() || "webkitdirectory" in document.createElement("input"));
};

// v2.22 (owner 2026-10-29, docs/rencana/FILE-MASTER-R2.md bagian 7.1): the design master files of one SPK.
// The server decides every right (`boleh`) and which versions this account sees (operators: final only).
export default function FileMaster({ spkId, testid = "fm" }) {
  const [d, setD] = React.useState(null);
  const [msg, setMsg] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [panel, setPanel] = React.useState(null); // {id, jenis: FINAL | BATAL_FINAL | HAPUS}
  const [teks, setTeks] = React.useState("");
  const [riwayat, setRiwayat] = React.useState({}); // id -> rows
  const [seret, setSeret] = React.useState(false);
  const { antrean, daftar: antre = [] } = useUnggahan() || {};
  const pilih = React.useRef(null); // <input multiple>: browsers without the file picker API
  const pilihFolderInput = React.useRef(null); // <input webkitdirectory>
  const pilihSatu = React.useRef(null); // "Lanjutkan" an unfinished version: that one file again
  const lanjutUntuk = React.useRef(null);

  const load = React.useCallback(() => apiFileMaster(spkId).then(setD).catch((e) => {
    if ([403, 404].includes(e.response?.status)) setD({ tersembunyi: true }); else setMsg(errText(e));
  }), [spkId]);
  React.useEffect(() => { load(); }, [load]);

  // v2.22.2: the queue (context, above the pages) uploads; this card reloads its list when a file of this SPK
  // gets its version, finishes or is refused
  const milik = antre.filter((x) => x.spkId === spkId);
  const tanda = milik.map((x) => `${x.id}:${x.status}:${x.fid || ""}`).join(",");
  const tandaLalu = React.useRef(tanda);
  React.useEffect(() => { if (tandaLalu.current !== tanda) { tandaLalu.current = tanda; load(); } }, [tanda, load]);

  if (!d) return msg ? <div className="text-xs text-red-300">{msg}</div> : null;
  if (d.tersembunyi) return null;
  const tampil = d.file || [];
  const pakai = finalTerbaru(tampil);
  const boleh = d.boleh || {};
  const bisaUnggah = !!(boleh.unggah && d.aktif && antrean);
  const diAntrean = milik.filter((x) => ![S.SELESAI, S.DITOLAK, S.DILEWATI, S.BATAL].includes(x.status)).length;

  const masukkan = (entri, cocok = null, lebih = false) => {
    if (!entri.length) return;
    const r = antrean.tambah(entri, { spkId, spkLabel: d.nomor_spk || "SPK", batas: d.batas, cocok });
    setMsg(`${r.masuk} file masuk antrean${r.dilewati ? `, ${r.dilewati} dilewati (alasannya di panel unggahan)` : ""}.`
      + (lebih ? ` Hanya ${MAKS_KUMPUL} file pertama yang dibaca dari folder.` : ""));
  };
  const pilihBanyakFile = async () => {
    if (adaPegangan()) {
      try { masukkan(await pilihBanyak(d.batas?.jenis)); return; } catch { /* picker refused: fall back to the plain input */ }
    }
    pilih.current?.click();
  };
  const pilihSatuFolder = async () => {
    if (adaPilihFolder()) {
      try { const r = await pilihFolder(); masukkan(r.entri, null, r.lebih); return; } catch { /* fall back */ }
    }
    pilihFolderInput.current?.click();
  };
  const lepas = async (e) => {
    e.preventDefault();
    setSeret(false);
    if (!bisaUnggah) return;
    const r = await dariDrop(e.dataTransfer); // asks for the handles during the event itself
    masukkan(r.entri, null, r.lebih);
  };
  const lanjutkanVersi = (f) => {
    const it = antrean?.cariFid(f.id);
    if (it && [S.JEDA, S.GAGAL].includes(it.status)) { antrean.lanjut(it.id); return; }
    lanjutUntuk.current = f;
    pilihSatu.current?.click();
  };
  const tindak = async (id, fn, sukses) => {
    setBusy(id); setMsg(null);
    try { await fn(); setMsg(sukses); setPanel(null); setTeks(""); await load(); }
    catch (e) { setMsg(errText(e)); await load(); } finally { setBusy(null); }
  };
  const unduh = async (f) => {
    setBusy(`u-${f.id}`); setMsg(null);
    try {
      const r = await apiFileMasterUnduh(f.id);
      const a = document.createElement("a"); // the link answers "attachment": the browser saves the file
      a.href = r.url; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove();
      setMsg(`Unduhan ${r.nama_file} dimulai (${ukuranTeks(r.ukuran_byte)}).`);
    } catch (e) { setMsg(errText(e)); } finally { setBusy(null); }
  };
  const bukaRiwayat = async (f) => {
    if (riwayat[f.id]) { setRiwayat((r) => { const n = { ...r }; delete n[f.id]; return n; }); return; }
    try { const rows = await apiFileMasterRiwayat(f.id); setRiwayat((r) => ({ ...r, [f.id]: rows })); }
    catch (e) { setMsg(errText(e)); }
  };
  const terima = ekstensiDiterima(d.batas?.jenis).join(",");

  return (
    <div data-testid={testid} data-seret={seret ? "1" : "0"}
      onDragOver={bisaUnggah ? (e) => { if (Array.from(e.dataTransfer?.types || []).includes("Files")) { e.preventDefault(); setSeret(true); } } : undefined}
      onDragLeave={bisaUnggah ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setSeret(false); } : undefined}
      onDrop={bisaUnggah ? lepas : undefined}
      className={`relative rounded-lg border p-3 space-y-2 text-sm ${seret ? "border-amber-500 bg-amber-500/10" : "border-slate-700 bg-slate-950/40"}`}>
      {seret && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-amber-500 bg-slate-950/80 text-sm font-semibold text-slate-100">
          Lepaskan file atau folder di sini</div>)}
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-200">File master desain</span>
        <span className="text-[11px] text-slate-500">file siap cetak untuk produksi; tidak tampil di halaman customer</span>
        {bisaUnggah && (
          <span className="ml-auto flex flex-wrap gap-1">
            <button data-testid={`${testid}-unggah`} type="button" onClick={pilihBanyakFile}
              className={`${TOMBOL} border-amber-500/50 text-slate-100 hover:bg-amber-500/10`}>
              <Upload size={13} /> Unggah file</button>
            {bisaFolder() && (
              <button data-testid={`${testid}-unggah-folder`} type="button" onClick={pilihSatuFolder}
                className={`${TOMBOL} border-slate-600 text-slate-200 hover:border-amber-500/50`}>
                <FolderUp size={13} /> Unggah folder</button>)}
          </span>
        )}
        <input ref={pilih} data-testid={`${testid}-file`} type="file" multiple className="hidden" accept={terima}
          onChange={(e) => { const fs = dariInput(e.target.files); e.target.value = ""; masukkan(fs); }} />
        <input ref={pilihFolderInput} data-testid={`${testid}-folder`} type="file" multiple className="hidden" webkitdirectory=""
          onChange={(e) => { const n = e.target.files?.length || 0; const fs = dariInput(e.target.files); e.target.value = ""; masukkan(fs, null, n > MAKS_KUMPUL); }} />
        <input ref={pilihSatu} data-testid={`${testid}-file-lanjut`} type="file" className="hidden" accept={terima}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f && lanjutUntuk.current) masukkan([{ file: f }], lanjutUntuk.current); }} />
      </div>

      {!d.aktif && (
        <div data-testid={`${testid}-mati`} className="flex items-center gap-2 rounded border border-slate-700 px-3 py-2 text-xs text-slate-400">
          <FileWarning size={14} /> Penyimpanan file master belum diaktifkan di server. Hubungi Owner.</div>
      )}

      {diAntrean > 0 && (
        <div data-testid={`${testid}-antrean`} className="rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-xs text-slate-300">
          {diAntrean} file untuk SPK ini ada di antrean unggahan. Kemajuannya di panel pojok kanan bawah; boleh pindah menu.</div>
      )}

      {tampil.length === 0 ? <p className="text-xs text-slate-500">Belum ada file master{boleh.unggah ? "" : " final"}.</p> : (
        <div className="divide-y divide-slate-800 rounded border border-slate-800">
          {tampil.map((f) => {
            const [label, warna] = labelStatus(f.status);
            const belum = f.status === "MENUNGGU_UNGGAH";
            const q = milik.find((x) => x.fid === f.id && ![S.SELESAI, S.DITOLAK, S.DILEWATI, S.BATAL].includes(x.status)); // in the upload queue
            return (
              <div key={f.id} data-testid={`${testid}-v${f.versi}`} className="p-2 space-y-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-mono text-xs font-semibold text-slate-300">v{f.versi}</span>
                  <span className="min-w-0 max-w-full truncate text-slate-200 sm:max-w-[18rem]" title={f.nama_file}>{f.nama_file}</span>
                  <span className="text-xs text-slate-500">{NAMA_JENIS[f.jenis] || f.jenis} · {ukuranTeks(f.ukuran_byte)}</span>
                  <span data-testid={`${testid}-v${f.versi}-status`} className={`rounded border px-1.5 py-0.5 text-[11px] ${warna}`}>{q ? ([S.PERIKSA, S.UNGGAH, S.SATUKAN].includes(q.status) ? "Sedang diunggah" : LABEL_ANTREAN[q.status]) : label}</span>
                  {f.id === pakai && <span data-testid={`${testid}-pakai`} className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-300"><CheckCircle2 size={12} /> pakai ini</span>}
                  <span className="text-[11px] text-slate-500">{f.diunggah_oleh} · {W.waktu(f.selesai_unggah || f.mulai_unggah)}{lamaUnggahTeks(f.durasi_unggah_detik) ? ` · unggah ${lamaUnggahTeks(f.durasi_unggah_detik)}` : ""}</span>
                  <span className="ml-auto flex flex-wrap gap-1">
                    {adaFile(f.status) && (
                      <button data-testid={`${testid}-v${f.versi}-unduh`} type="button" disabled={busy === `u-${f.id}`} onClick={() => unduh(f)} className={`${TOMBOL} border-slate-600 text-slate-200 hover:border-amber-500/50`}>
                        {busy === `u-${f.id}` ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Unduh</button>)}
                    {boleh.final && f.status === "DRAF" && (
                      <button data-testid={`${testid}-v${f.versi}-final`} type="button" onClick={() => { setPanel({ id: f.id, jenis: "FINAL" }); setTeks(""); }} className={`${TOMBOL} border-emerald-500/50 text-emerald-300`}><CheckCircle2 size={12} /> Tandai final</button>)}
                    {boleh.batal_final && f.status === "FINAL" && (
                      <button data-testid={`${testid}-v${f.versi}-batal-final`} type="button" onClick={() => { setPanel({ id: f.id, jenis: "BATAL_FINAL" }); setTeks(""); }} className={`${TOMBOL} border-slate-600 text-slate-300`}><Undo2 size={12} /> Batal final</button>)}
                    {boleh.hapus && adaFile(f.status) && (
                      <button data-testid={`${testid}-v${f.versi}-hapus`} type="button" onClick={() => { setPanel({ id: f.id, jenis: "HAPUS" }); setTeks(""); }} className={`${TOMBOL} border-red-500/40 text-red-300`}><Trash2 size={12} /> Hapus</button>)}
                    {belum && f.milik_saya && bisaUnggah && (!q || [S.JEDA, S.GAGAL].includes(q.status)) && (
                      <button data-testid={`${testid}-v${f.versi}-lanjutkan`} type="button" onClick={() => lanjutkanVersi(f)} className={`${TOMBOL} border-amber-500/50 text-slate-100`}><Play size={12} /> Lanjutkan</button>)}
                    {belum && (f.milik_saya || boleh.hapus) && (
                      <button data-testid={`${testid}-v${f.versi}-batalkan`} type="button" disabled={busy === f.id || q?.status === S.SATUKAN}
                        onClick={() => window.confirm(`Batalkan unggahan v${f.versi}? Bagian yang sudah terkirim dibuang.`) && (q ? antrean.batal(q.id) : tindak(f.id, () => apiFileMasterBatal(f.id), `Unggahan v${f.versi} dibatalkan`))}
                        className={`${TOMBOL} border-slate-600 text-slate-300`}><X size={12} /> Batalkan</button>)}
                    {boleh.riwayat && (
                      <button data-testid={`${testid}-v${f.versi}-riwayat`} type="button" onClick={() => bukaRiwayat(f)} className={`${TOMBOL} border-slate-700 text-slate-400`}><History size={12} /> Riwayat</button>)}
                  </span>
                </div>
                {belum && f.milik_saya && !q && <div className="text-[11px] text-amber-300">Unggahan ini belum selesai. Tekan Lanjutkan lalu pilih file yang sama ({f.nama_file}, {ukuranTeks(f.ukuran_byte)}): hanya bagian yang belum terkirim yang dikirim.</div>}
                {f.catatan_final && <div className="text-[11px] text-slate-400">Catatan final: {f.catatan_final}</div>}
                {adaFile(f.status) && f.hapus_otomatis_pada && <div className="text-[11px] text-slate-500">Dihapus otomatis {W.tanggal(f.hapus_otomatis_pada)}</div>}
                {f.alasan_hapus && <div className="text-[11px] text-slate-500">Alasan hapus: {f.alasan_hapus}{f.dihapus_oleh ? ` (${f.dihapus_oleh})` : ""}</div>}
                {panel?.id === f.id && (
                  <div data-testid={`${testid}-panel`} className="rounded border border-slate-700 bg-slate-900/60 p-2 space-y-1.5">
                    <div className="text-xs text-slate-200">
                      {panel.jenis === "FINAL" ? `File ini yang dicetak? v${f.versi} menjadi final dan disimpan ${d.batas?.final_hari} hari.`
                        : panel.jenis === "BATAL_FINAL" ? `Batalkan final v${f.versi}? File kembali menjadi draf (disimpan ${d.batas?.draf_hari} hari).`
                          : `Hapus v${f.versi} dari penyimpanan? File tidak bisa dikembalikan; catatannya tetap ada.${f.status === "FINAL" ? " Perhatian: versi final ini mungkin sedang dipakai produksi." : ""}`}
                    </div>
                    <input data-testid={`${testid}-panel-teks`} value={teks} onChange={(e) => setTeks(e.target.value)} maxLength={300}
                      placeholder={panel.jenis === "FINAL" ? "Catatan (boleh kosong), mis. siap cetak sublim" : "Alasan (wajib, minimal 5 huruf)"}
                      className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200" />
                    <div className="flex gap-1">
                      <button data-testid={`${testid}-panel-ya`} type="button" disabled={busy === f.id || (panel.jenis !== "FINAL" && teks.trim().length < 5)}
                        onClick={() => tindak(f.id,
                          () => (panel.jenis === "FINAL" ? apiFileMasterFinal(f.id, teks.trim()) : panel.jenis === "BATAL_FINAL" ? apiFileMasterBatalFinal(f.id, teks.trim()) : apiFileMasterHapus(f.id, teks.trim())),
                          panel.jenis === "FINAL" ? `v${f.versi} ditandai final` : panel.jenis === "BATAL_FINAL" ? `Final v${f.versi} dibatalkan` : `v${f.versi} dihapus`)}
                        className={`${TOMBOL} ${panel.jenis === "HAPUS" ? "border-red-500/50 text-red-300" : "border-amber-500/50 text-slate-100"}`}>
                        {busy === f.id && <Loader2 size={12} className="animate-spin" />}{panel.jenis === "FINAL" ? "Ya, tandai final" : panel.jenis === "BATAL_FINAL" ? "Ya, batalkan final" : "Ya, hapus"}</button>
                      <button type="button" onClick={() => setPanel(null)} className={`${TOMBOL} border-slate-700 text-slate-400`}>Batal</button>
                    </div>
                  </div>
                )}
                {riwayat[f.id] && (
                  <ul data-testid={`${testid}-v${f.versi}-riwayat-isi`} className="rounded border border-slate-800 p-2 text-[11px] text-slate-400 space-y-0.5">
                    {riwayat[f.id].length === 0 && <li>Belum ada catatan.</li>}
                    {riwayat[f.id].map((r, i) => (
                      <li key={i}>{W.waktu(r.waktu)} · {LABEL_AKSI[r.aksi] || r.aksi} · {r.oleh}{r.rincian?.alasan ? ` · ${r.rincian.alasan}` : ""}{r.rincian?.ip ? ` · ${r.rincian.ip}` : ""}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
      {bisaUnggah && (
        <p className="text-[11px] text-slate-500">
          {(d.batas?.jenis || []).map((j) => NAMA_JENIS[j] || j).join(", ")} · paling besar {ukuranTeks((d.batas?.maks_mb || 0) * 1024 * 1024)} · draf disimpan {d.batas?.draf_hari} hari, final {d.batas?.final_hari} hari.
          Boleh pilih banyak file sekaligus, satu folder, atau seret file/folder ke kotak ini. Unggah dari komputer dianjurkan.</p>
      )}
      {msg && <div data-testid={`${testid}-pesan`} className="text-xs text-slate-300">{msg}</div>}
    </div>
  );
}
